import type { Accomplishment } from '@clan-heart/shared';

/** The viewer's relationship to one accomplishment, as the card renders it. */
export type ClaimState = 'unclaimed' | 'pending' | 'approved' | 'rejected';

export function claimStateFor(myClaim: Accomplishment['myClaim']): ClaimState {
  if (myClaim === null) return 'unclaimed';
  return myClaim.status;
}

const LABELS: Record<ClaimState, string> = {
  unclaimed: 'Claim',
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};

export function claimStateLabel(myClaim: Accomplishment['myClaim']): string {
  return LABELS[claimStateFor(myClaim)];
}

/**
 * Whether the viewer may submit a new claim on this accomplishment.
 *
 * Non-repeatable: only when there is no live (non-rejected) claim — i.e.
 * they have never claimed it, or their one claim was rejected (a rejected
 * claim never blocks a retry).
 *
 * Repeatable: any number of approved claims are allowed — each repeat is a
 * new claim — so the only thing that blocks another is a claim still
 * awaiting review. `myClaim` on a repeatable accomplishment is the pending
 * claim if one exists, else the most recent (possibly approved or
 * rejected) claim, so "not pending" is exactly the right check.
 */
export function canClaimAgain(accomplishment: Pick<Accomplishment, 'repeatable' | 'myClaim'>): boolean {
  const { repeatable, myClaim } = accomplishment;
  if (repeatable) return myClaim === null || myClaim.status !== 'pending';
  const state = claimStateFor(myClaim);
  return state === 'unclaimed' || state === 'rejected';
}
